import {tokenize} from 'utils/plagiarismMatcher';

// A word needs this many letters to count on its own — shorter ones ("the", "of")
// are everywhere and say nothing about which sentence a rewrite came from; a bigram
// counts by how many of its two words do.
const CONTENT_WORD_MIN = 4;
const UNIGRAM_WEIGHT = 0.25;
// A neighbour joins the match when it carries a real share of the query's words:
// a rewrite that split one sentence in two, or merged two into one.
const NEIGHBOUR_MIN_SCORE = 2;
const NEIGHBOUR_MIN_SHARE = 0.35;

const isContent = (word: string) => word.length >= CONTENT_WORD_MIN;

const words = (text: string): string[] => tokenize(text).map(token => token.normalized);

/** The bigrams of a word list, each with its weight. */
const bigrams = (list: string[]): Map<string, number> => {
    const map = new Map<string, number>();
    for (let i = 1; i < list.length; i++) {
        map.set(`${list[i - 1]}|${list[i]}`, (Number(isContent(list[i - 1])) + Number(isContent(list[i]))) / 2);
    }
    return map;
};

/**
 * Which of `candidates` a sentence corresponds to, as indices — the sentence the
 * words came from (or went to), and its neighbours when the rewrite split or merged
 * sentences. Scored on shared word bigrams, with a little weight on shared longer
 * words so a heavily reworded sentence still finds its origin. Empty when nothing
 * is shared.
 */
export const alignSentence = (query: string, candidates: string[]): number[] => {
    const queryWords = words(query);
    const queryBigrams = bigrams(queryWords);
    const queryContent = new Set(queryWords.filter(isContent));
    if (!queryBigrams.size && !queryContent.size) return [];

    const scores = candidates.map(candidate => {
        const list = words(candidate);
        let score = 0;
        bigrams(list).forEach((_, gram) => { score += queryBigrams.get(gram) ?? 0; });
        new Set(list).forEach(w => { if (queryContent.has(w)) score += UNIGRAM_WEIGHT; });
        return score;
    });

    let best = -1;
    scores.forEach((score, i) => { if (score > (best < 0 ? 0 : scores[best])) best = i; });
    if (best < 0 || scores[best] < 1) return [];

    const joins = (i: number) => i >= 0 && i < scores.length
        && scores[i] >= NEIGHBOUR_MIN_SCORE && scores[i] >= scores[best] * NEIGHBOUR_MIN_SHARE;
    let start = best;
    let end = best;
    while (joins(start - 1)) start--;
    while (joins(end + 1)) end++;
    const result: number[] = [];
    for (let i = start; i <= end; i++) result.push(i);
    return result;
};
